import React, { useCallback, useEffect, useState } from 'react';
import './TabsHeader.scss';
import { SingleUser, UserAccountRoleEnum, UserRelationRoleEnum } from '../../api/types';
import { DossierMember, DossierMemberInterface } from '../../models/DossierMember';
import { Api } from '../../api';
import { Icon } from '../../features/icon/Icon';
import { Avatar } from '../../features/avatar/Avatar';
import { ERouterPath } from '../../features/router/ERouterPath';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { IDossierV2 } from '../../models/DossierV2';

interface TabsHeaderProps {
  className?: string;
}

export function TabsHeader({ className }: TabsHeaderProps): JSX.Element {
  const [dossierRelations, setDossierRelations] = useState<DossierMember[]>([]);
  const dossier = useSelector<ApplicationState, IDossierV2|undefined>(state => state.session.dossier);
  const dossierRelationData = useSelector<ApplicationState, DossierMemberInterface|undefined>(state => state.session.relation);
  const user = useSelector<ApplicationState, SingleUser|undefined>(state => state.session.user);

  const dossierRelation = dossierRelationData && new DossierMember(dossierRelationData);

  const fullClassName = `tabs-header ${className ? className : ''}`;

  const isAdminCaregiver = user?.accountRole === UserAccountRoleEnum.adminCaregiver;
  const isCaregiver = user?.accountRole === UserAccountRoleEnum.caregiver || isAdminCaregiver;
  const isAdmin = user?.accountRole === UserAccountRoleEnum.administration || isAdminCaregiver;

  const caregivers = dossierRelations && dossierRelations.filter(rel => rel.role === UserRelationRoleEnum.caregiver);

  const hasDossierSelected = !!(dossier);
  const canSeeCareweb = isCaregiver || isAdmin;

  const history = useHistory();

  const handleClick = (goTo: ERouterPath|string) => {
    history.push(goTo);
  };

  const loadDossierRelations = useCallback(() => {
    if (!dossier) {
      return;
    }

    Api.listDossierRelations(dossier.id)
      .then(relations => {
        setDossierRelations(relations);
      })
      .catch(err => console.error('Failed to load dossier relations', err));
  }, [dossier]);

  useEffect(() => {
    loadDossierRelations();
  }, [loadDossierRelations]);

  const renderCaregivers = (): JSX.Element[] => caregivers.map((caregiver, index) => (
    <span key={index} className="tabs-header__caregiver">
      <Avatar user={caregiver.user}/>{caregiver.user.name || ''} | Behandelaar
    </span>
  ));

  const showEditDossierButton = !dossier?.archived && (user && [UserAccountRoleEnum.adminCaregiver, UserAccountRoleEnum.administration].includes(user.accountRole));

  return (
    <div className={fullClassName}>
      <div className="tabs-header__title">
        { dossier?.archived && <span className="tabs-header__archived">Gearchiveerd</span> }
        <h2>
          <span className="tabs-header__prefix-user">Dossier </span>
          { dossier?.name }
          { showEditDossierButton && <button className="tabs-header__edit-dossier-button ico-btn" title="Dossier aanpassen" onClick={() => history.push(ERouterPath.updateDossier)}><Icon icon="edit"></Icon></button> }
        </h2>
        { dossierRelation && <div className="tabs-header__role">Jouw rol in het dossier is { dossierRelation.getRoleText() }</div> }
      </div>
      <div className="tabs-header__navigation">
        <div className="tabs-header__dossier-info-wrapper">
          <div className="tabs-header__caregivers">
            { caregivers && renderCaregivers() }
          </div>
          {
            hasDossierSelected &&
              <div className="tabs-header__participants">
                <button className="tabs-header__button-participants" onClick={() => handleClick(ERouterPath.manageDossier)}>
                  <Icon icon="participants"></Icon>
                    Alle deelnemers
                </button>
              </div>
          }
        </div>
        <div className="tabs-header__contact-container">
          <a href="https://web.zaurus.io/session" className="tabs-header__contact tabs-header__contact--video" target="_blank" rel="noopener noreferrer">
            <Icon icon="videocam"></Icon>
            <span>Beeldbellen</span>
          </a>
          {
            canSeeCareweb &&
            <a href="https://sms.careweb.net/" className="tabs-header__contact tabs-header__contact__careweb" target="_blank" rel="noopener noreferrer">
              <Icon icon="careweb"></Icon>
              <span>Careweb</span>
            </a>
          }
        </div>
      </div>
    </div>
  );
}
